import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Coffee, MapPin, Clock, Store, Truck, ShoppingBag, ChevronLeft, Check, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api, { apiError } from "@/lib/api";
import { eur } from "@/lib/status";
import MiniMap from "@/components/MiniMap";

export default function GuestOrder() {
  const { bnbId } = useParams();
  const [params] = useSearchParams();
  const room = params.get("room") || "";
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [step, setStep] = useState(1);
  const [vendor, setVendor] = useState(null);
  const [pkg, setPkg] = useState(null);
  const [mode, setMode] = useState("pickup");
  const [persons, setPersons] = useState(1);
  const [form, setForm] = useState({ guest_name: "", guest_phone: "", date: "", time_slot: "08:00 - 09:00", notes: "", delivery_address: "" });
  const [payment, setPayment] = useState("property");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/guest/bnb/${bnbId}${room ? `?room=${room}` : ""}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ error: true }));
  }, [bnbId, room]);

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="w-8 h-8 text-orange animate-spin" /></div>;
  if (data.error) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6 text-center">
      <Coffee className="w-12 h-12 text-orange" />
      <h1 className="mt-4 text-xl font-bold text-navy">Struttura non disponibile</h1>
      <p className="mt-2 text-navy/60">Il QR code non è valido o il servizio non è ancora attivo.</p>
    </div>
  );

  const guestPrice = pkg ? pkg.guest_price_pp : 0;
  const delivery = mode === "delivery" && vendor ? (vendor.delivery_cost || 0) : 0;
  const total = guestPrice * persons + delivery;

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        bnb_id: bnbId, vendor_id: vendor.id, package_id: pkg.id, room,
        guest_name: form.guest_name, guest_phone: form.guest_phone, persons: Number(persons),
        date: form.date, time_slot: form.time_slot, mode, delivery_address: form.delivery_address,
        notes: form.notes, payment_method: payment, origin_url: window.location.origin,
      };
      const { data: res } = await api.post("/guest/orders", payload);
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      toast.success("Ordine inviato!");
      navigate(`/ordine/${res.order.code}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-navy text-white px-5 py-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3">
            {data.bnb.logo ? <img src={data.bnb.logo} alt="" className="w-11 h-11 rounded-xl object-cover" /> : <div className="w-11 h-11 rounded-xl bg-orange flex items-center justify-center"><Coffee className="w-6 h-6 text-white" /></div>}
            <div>
              <p className="font-heading font-bold text-lg leading-tight">{data.bnb.name}</p>
              {data.room && <p className="text-white/60 text-sm">Camera {data.room.number}</p>}
            </div>
          </div>
          <p className="mt-3 text-white/80 text-sm">{data.room?.greeting || "Benvenuto! Ordina la tua colazione."}</p>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 py-6">
        <Steps step={step} />

        {/* Step 1: choose vendor */}
        {step === 1 && (
          <div className="space-y-3">
            <h2 className="font-bold text-navy text-lg">Scegli il locale</h2>
            {data.bnb.lat != null && data.vendors.length > 0 && (
              <MiniMap center={[data.bnb.lat, data.bnb.lng]} markers={data.vendors} />
            )}
            {data.vendors.length === 0 && <p className="text-navy/50 text-sm">Nessun locale convenzionato al momento.</p>}
            {data.vendors.map((v) => (
              <button key={v.id} onClick={() => { setVendor(v); setStep(2); }} data-testid={`guest-vendor-${v.id}`}
                className="w-full text-left bg-white rounded-2xl p-4 border border-navy/10 hover:border-orange transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-cream flex items-center justify-center overflow-hidden shrink-0">
                    {v.logo ? <img src={v.logo} alt="" className="w-full h-full object-cover" /> : <Store className="w-6 h-6 text-orange" />}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-navy">{v.name}</p>
                    <p className="text-xs text-navy/50 capitalize">{v.type} · {v.city}{v.distance_km != null ? ` · ${v.distance_km} km` : ""}</p>
                    <div className="flex gap-3 mt-1 text-xs text-navy/50">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{v.hours || "—"}</span>
                      <span className="flex items-center gap-1">{v.offers_delivery ? <Truck className="w-3 h-3" /> : <ShoppingBag className="w-3 h-3" />}{v.offers_delivery && v.offers_pickup ? "Ritiro + consegna" : v.offers_delivery ? "Consegna" : "Ritiro"}</span>
                    </div>
                  </div>
                </div>
                {v.photos?.length > 0 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto" data-testid={`vendor-gallery-${v.id}`}>
                    {v.photos.slice(0, 5).map((ph, i) => (
                      <img key={i} src={ph} alt="" className="w-20 h-20 rounded-xl object-cover shrink-0 border border-navy/10" />
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Step 2: choose package */}
        {step === 2 && vendor && (
          <div className="space-y-3">
            <BackBtn onClick={() => setStep(1)} />
            <h2 className="font-bold text-navy text-lg">Scegli il pacchetto</h2>
            {vendor.packages.map((p) => (
              <button key={p.id} onClick={() => { setPkg(p); setStep(3); }} data-testid={`guest-package-${p.id}`}
                className="w-full text-left bg-white rounded-2xl p-4 border border-navy/10 hover:border-orange transition-colors">
                <div className="flex justify-between items-start">
                  <p className="font-semibold text-navy">{p.name}</p>
                  <span className="font-bold text-orange">{eur(p.guest_price_pp)}<span className="text-xs text-navy/40 font-normal"> /pers.</span></span>
                </div>
                <p className="text-xs text-navy/50 mt-1">{p.description}</p>
                {p.allergens?.length > 0 && <p className="text-[11px] text-navy/40 mt-2">Allergeni: {p.allergens.join(", ")}</p>}
              </button>
            ))}
          </div>
        )}

        {/* Step 3: mode + details */}
        {step === 3 && pkg && (
          <div className="space-y-4">
            <BackBtn onClick={() => setStep(2)} />
            <h2 className="font-bold text-navy text-lg">Modalità e dettagli</h2>

            <div className="bg-white rounded-2xl p-4 border border-navy/10">
              <Label className="text-navy/70">Modalità</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {vendor.offers_pickup && <ModeBtn active={mode === "pickup"} onClick={() => setMode("pickup")} icon={ShoppingBag} label="Ritiro al bar" tid="mode-pickup" />}
                {vendor.offers_delivery && <ModeBtn active={mode === "delivery"} onClick={() => setMode("delivery")} icon={Truck} label="Consegna in camera" tid="mode-delivery" />}
              </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-navy/10 flex items-center justify-between">
              <Label className="text-navy/70">Numero di persone</Label>
              <div className="flex items-center gap-3">
                <button onClick={() => setPersons(Math.max(1, persons - 1))} className="w-9 h-9 rounded-full bg-cream flex items-center justify-center" data-testid="persons-minus"><Minus className="w-4 h-4 text-navy" /></button>
                <span className="font-bold text-navy w-6 text-center" data-testid="persons-count">{persons}</span>
                <button onClick={() => setPersons(persons + 1)} className="w-9 h-9 rounded-full bg-cream flex items-center justify-center" data-testid="persons-plus"><Plus className="w-4 h-4 text-navy" /></button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Giorno</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="guest-date" /></div>
              <div><Label>Fascia oraria</Label>
                <select value={form.time_slot} onChange={(e) => setForm({ ...form, time_slot: e.target.value })} className="w-full rounded-xl mt-1 h-11 border border-input px-3 text-sm" data-testid="guest-slot">
                  {["07:00 - 08:00", "08:00 - 09:00", "09:00 - 10:00", "10:00 - 11:00"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div><Label>Nome</Label><Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="guest-name" /></div>
            <div><Label>Telefono</Label><Input value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="rounded-xl mt-1 h-11" data-testid="guest-phone" /></div>
            {mode === "delivery" && <div><Label>Indirizzo di consegna</Label><Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} className="rounded-xl mt-1 h-11" placeholder={`${data.bnb.name}, camera ${data.room?.number || ""}`} data-testid="guest-address" /></div>}
            <div><Label>Note (facoltative)</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" rows={2} data-testid="guest-notes" /></div>

            <Button onClick={() => { if (!form.guest_name || !form.date) { toast.error("Inserisci nome e giorno"); return; } setStep(4); }} className="w-full rounded-full bg-navy text-white h-12" data-testid="guest-to-summary">
              Vai al riepilogo
            </Button>
          </div>
        )}

        {/* Step 4: summary + payment */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <BackBtn onClick={() => setStep(3)} />
            <h2 className="font-bold text-navy text-lg">Riepilogo ordine</h2>
            <div className="bg-white rounded-2xl p-5 border border-navy/10 space-y-2 text-sm">
              <Row k="Locale" v={vendor.name} />
              <Row k="Pacchetto" v={pkg.name} />
              <Row k="Persone" v={persons} />
              <Row k="Modalità" v={mode === "delivery" ? "Consegna in camera" : "Ritiro al bar"} />
              <Row k="Giorno" v={form.date} />
              <Row k="Fascia oraria" v={form.time_slot} />
              <div className="border-t border-navy/10 my-2" />
              <Row k={`Colazione (${eur(guestPrice)} × ${persons})`} v={eur(guestPrice * persons)} />
              {delivery > 0 && <Row k="Consegna" v={eur(delivery)} />}
              <div className="flex justify-between font-bold text-navy text-base pt-1"><span>Totale</span><span className="text-orange" data-testid="guest-total">{eur(total)}</span></div>
              <p className="text-[11px] text-navy/40">Prezzi a persona.</p>
            </div>

            <div className="bg-white rounded-2xl p-5 border border-navy/10">
              <Label className="text-navy/70">Metodo di pagamento</Label>
              <RadioGroup value={payment} onValueChange={setPayment} className="mt-3 space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-xl border border-navy/10 cursor-pointer" data-testid="pay-property">
                  <RadioGroupItem value="property" /> <span className="text-sm text-navy">Pagamento in struttura</span>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-xl border border-navy/10 cursor-pointer" data-testid="pay-stripe">
                  <RadioGroupItem value="stripe" /> <span className="text-sm text-navy">Paga ora con carta</span>
                </label>
              </RadioGroup>
            </div>

            <Button onClick={submit} disabled={submitting} className="w-full rounded-full bg-orange hover:bg-orange-600 text-white h-12 text-base" data-testid="guest-confirm">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : payment === "stripe" ? `Paga ${eur(total)}` : "Conferma ordine"}
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

function Steps({ step }) {
  return (
    <div className="flex items-center gap-1.5 mb-6">
      {[1, 2, 3, 4].map((n) => (
        <div key={n} className={`h-1.5 flex-1 rounded-full transition-colors ${n <= step ? "bg-orange" : "bg-navy/10"}`} />
      ))}
    </div>
  );
}
function BackBtn({ onClick }) {
  return <button onClick={onClick} className="flex items-center gap-1 text-navy/60 text-sm" data-testid="guest-back"><ChevronLeft className="w-4 h-4" /> Indietro</button>;
}
function ModeBtn({ active, onClick, icon: Icon, label, tid }) {
  return (
    <button onClick={onClick} data-testid={tid} className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border transition-colors ${active ? "border-orange bg-orange/10" : "border-navy/10"}`}>
      <Icon className={`w-5 h-5 ${active ? "text-orange" : "text-navy/50"}`} />
      <span className={`text-xs font-medium ${active ? "text-orange" : "text-navy/60"}`}>{label}</span>
      {active && <Check className="w-3 h-3 text-orange" />}
    </button>
  );
}
function Row({ k, v }) {
  return <div className="flex justify-between text-navy/70"><span>{k}</span><span className="font-medium text-navy">{v}</span></div>;
}
